import type { ViableFormulasResponse } from '@/lib/api/clients';

type BiologicalSex = 'male' | 'female' | '';
type BackendAllowedLevels = ViableFormulasResponse['allowedActivityLevels'];

type ActivityLevelKey =
  | 'sedentary'
  | 'lightly_active'
  | 'moderately_active'
  | 'moderate'
  | 'active'
  | 'very_active'
  | 'extremely_active'
  | 'athlete_boys'
  | 'athlete_girls';

interface ActivityLevelDefinition {
  key: ActivityLevelKey;
  value: string;
  label: string;
  palValue: number;
  description?: string;
  isDefault?: boolean;
  iomPaMale?: number;
  iomPaFemale?: number;
  athleteForSex?: Exclude<BiologicalSex, ''>;
}

interface ActivityLevelAgeProfile {
  minAge: number;
  maxAge: number | null;
  ageGroup: string;
  options: ActivityLevelDefinition[];
}

export interface ActivityLevelOption {
  value: string;
  label: string;
  description: string;
  palValue: number;
  isDefault: boolean;
  iomPaMale?: number;
  iomPaFemale?: number;
}

export interface ResolvedActivityLevels {
  ageGroup: string;
  options: ActivityLevelOption[];
}

export interface ActivityLevelSelectOption {
  value: string;
  label: string;
  description?: string;
}

const ADULT_ACTIVITY_DESCRIPTIONS: Record<'sedentary' | 'lightly_active' | 'moderately_active' | 'active' | 'very_active' | 'extremely_active', string> = {
  sedentary: 'Desk-based or seated occupation, little to no daily walking, no structured exercise, non-athlete',
  lightly_active:
    'Desk job with light exercise 1-3 days/week, OR standing/light walking occupation with no formal exercise; recreational mover, not in competitive training',
  moderately_active:
    'Desk job with moderate exercise 3-5 days/week, OR active occupation such as teaching or retail with light exercise; recreational athlete training for general fitness',
  active:
    'Active occupation such as nursing, hospitality, or field sales with moderate exercise 3-5 days/week, OR desk job with hard daily training; competitive skill-based or team-sport athlete off-season',
  very_active:
    'Physically demanding occupation such as construction or trades with hard training 6 days/week, OR moderate occupation with twice-daily structured training; competitive strength, power, or team-sport athlete in-season',
  extremely_active:
    'Heavy manual labour with daily intense training, OR endurance/ultra-endurance athletes in heavy training blocks such as marathon prep, triathlon, professional cycling or swimming',
};

const ACTIVITY_LEVEL_AGE_PROFILES: ActivityLevelAgeProfile[] = [
  {
    minAge: 1,
    maxAge: 3,
    ageGroup: '1-3 years',
    options: [
      { key: 'sedentary', value: 'sedentary', label: 'Sedentary', palValue: 1.4 },
      { key: 'active', value: 'active', label: 'Active', palValue: 1.45, isDefault: true },
    ],
  },
  {
    minAge: 4,
    maxAge: 9,
    ageGroup: '4-9 years',
    options: [
      { key: 'sedentary', value: 'sedentary', label: 'Sedentary', palValue: 1.4, iomPaMale: 1, iomPaFemale: 1 },
      { key: 'lightly_active', value: 'lightly_active', label: 'Lightly Active', palValue: 1.5, iomPaMale: 1.13, iomPaFemale: 1.16 },
      { key: 'moderate', value: 'moderately_active', label: 'Moderate', palValue: 1.6, isDefault: true, iomPaMale: 1.26, iomPaFemale: 1.31 },
      { key: 'active', value: 'active', label: 'Active', palValue: 1.75, iomPaMale: 1.42, iomPaFemale: 1.56 },
    ],
  },
  {
    minAge: 10,
    maxAge: 18,
    ageGroup: '10-18 years',
    options: [
      { key: 'sedentary', value: 'sedentary', label: 'Sedentary', palValue: 1.4, iomPaMale: 1, iomPaFemale: 1 },
      { key: 'lightly_active', value: 'lightly_active', label: 'Lightly Active', palValue: 1.55, iomPaMale: 1.13, iomPaFemale: 1.16 },
      { key: 'moderate', value: 'moderately_active', label: 'Moderate', palValue: 1.6, iomPaMale: 1.26, iomPaFemale: 1.31 },
      { key: 'active', value: 'active', label: 'Active', palValue: 1.75, isDefault: true, iomPaMale: 1.26, iomPaFemale: 1.31 },
      { key: 'very_active', value: 'very_active', label: 'Very Active', palValue: 1.8, iomPaMale: 1.42, iomPaFemale: 1.56 },
      {
        key: 'athlete_boys',
        value: 'athlete_boys',
        label: 'Athlete - Boys',
        palValue: 2.1,
        iomPaMale: 1.42,
        iomPaFemale: 1.56,
        athleteForSex: 'male',
      },
      {
        key: 'athlete_girls',
        value: 'athlete_girls',
        label: 'Athlete - Girls',
        palValue: 1.9,
        iomPaMale: 1.42,
        iomPaFemale: 1.56,
        athleteForSex: 'female',
      },
    ],
  },
  {
    minAge: 19,
    maxAge: 59,
    ageGroup: '19-59 years',
    options: [
      { key: 'sedentary', value: 'sedentary', label: 'Sedentary', palValue: 1.25, description: ADULT_ACTIVITY_DESCRIPTIONS.sedentary, iomPaMale: 1, iomPaFemale: 1 },
      { key: 'lightly_active', value: 'lightly_active', label: 'Lightly Active', palValue: 1.4, description: ADULT_ACTIVITY_DESCRIPTIONS.lightly_active, iomPaMale: 1.11, iomPaFemale: 1.12 },
      { key: 'moderately_active', value: 'moderately_active', label: 'Moderately Active', palValue: 1.55, description: ADULT_ACTIVITY_DESCRIPTIONS.moderately_active, iomPaMale: 1.25, iomPaFemale: 1.27 },
      { key: 'active', value: 'active', label: 'Active', palValue: 1.7, description: ADULT_ACTIVITY_DESCRIPTIONS.active, iomPaMale: 1.25, iomPaFemale: 1.27 },
      { key: 'very_active', value: 'very_active', label: 'Very Active', palValue: 1.85, description: ADULT_ACTIVITY_DESCRIPTIONS.very_active, iomPaMale: 1.48, iomPaFemale: 1.45 },
      { key: 'extremely_active', value: 'extremely_active', label: 'Extra Active', palValue: 2.0, description: ADULT_ACTIVITY_DESCRIPTIONS.extremely_active, iomPaMale: 1.48, iomPaFemale: 1.45 },
    ],
  },
  {
    minAge: 60,
    maxAge: null,
    ageGroup: '60+ years',
    options: [
      { key: 'sedentary', value: 'sedentary', label: 'Sedentary', palValue: 1.25, description: ADULT_ACTIVITY_DESCRIPTIONS.sedentary, iomPaMale: 1, iomPaFemale: 1 },
      { key: 'lightly_active', value: 'lightly_active', label: 'Lightly Active', palValue: 1.4, description: ADULT_ACTIVITY_DESCRIPTIONS.lightly_active, iomPaMale: 1.11, iomPaFemale: 1.12 },
      { key: 'moderately_active', value: 'moderately_active', label: 'Moderately Active', palValue: 1.55, description: ADULT_ACTIVITY_DESCRIPTIONS.moderately_active, iomPaMale: 1.25, iomPaFemale: 1.27 },
      { key: 'active', value: 'active', label: 'Active', palValue: 1.7, description: ADULT_ACTIVITY_DESCRIPTIONS.active, iomPaMale: 1.25, iomPaFemale: 1.27 },
      { key: 'very_active', value: 'very_active', label: 'Very Active', palValue: 1.85, description: ADULT_ACTIVITY_DESCRIPTIONS.very_active, iomPaMale: 1.48, iomPaFemale: 1.45 },
      { key: 'extremely_active', value: 'extremely_active', label: 'Extra Active', palValue: 2.0, description: ADULT_ACTIVITY_DESCRIPTIONS.extremely_active, iomPaMale: 1.48, iomPaFemale: 1.45 },
    ],
  },
];

const toNormalizedToken = (value: string): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/[\s-]+/g, '_');

export const getActivityLevelKey = (value: string): ActivityLevelKey | null => {
  const token = toNormalizedToken(value);
  if (!token) return null;

  if (token.includes('athlete')) {
    if (token.includes('boy') || token.includes('male')) return 'athlete_boys';
    if (token.includes('girl') || token.includes('female')) return 'athlete_girls';
  }

  if (token.includes('sedentary')) return 'sedentary';
  if (token.includes('lightly_active') || token.includes('low_active') || token === 'light_active') return 'lightly_active';
  if (token.includes('very_active') || token.includes('highly_active')) return 'very_active';
  if (token.includes('extremely_active') || token.includes('extra_active')) return 'extremely_active';
  if (token.includes('moderately_active')) return 'moderately_active';
  if (token === 'moderate' || token.startsWith('moderate_') || token.includes('_moderate')) return 'moderate';

  // Keep this check last so that "lightly active", "very active", etc. are not captured as plain active.
  if (token === 'active' || token.startsWith('active_')) return 'active';

  return null;
};

const getEquivalentKeys = (key: ActivityLevelKey): ActivityLevelKey[] => {
  if (key === 'moderately_active') return ['moderately_active', 'moderate'];
  if (key === 'moderate') return ['moderate', 'moderately_active'];
  return [key];
};

const getAgeProfile = (age: number): ActivityLevelAgeProfile | null =>
  ACTIVITY_LEVEL_AGE_PROFILES.find(
    (profile) => age >= profile.minAge && (profile.maxAge === null || age <= profile.maxAge)
  ) ?? null;

const shouldIncludeOptionForSex = (option: ActivityLevelDefinition, biologicalSex: BiologicalSex): boolean => {
  if (!option.athleteForSex) return true;
  if (!biologicalSex) return true;
  return option.athleteForSex === biologicalSex;
};

const formatActivityDescription = (palValue: number, iomPaMale?: number, iomPaFemale?: number): string => {
  const palPart = `PAL ${palValue.toFixed(2)}`;
  if (typeof iomPaMale === 'number' && typeof iomPaFemale === 'number') {
    return `${palPart} | IOM PA (M/F): ${iomPaMale.toFixed(2)} / ${iomPaFemale.toFixed(2)}`;
  }
  return palPart;
};

const toDisplayLabelFromValue = (value: string): string =>
  String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeBackendOnlyOptions = (backendAllowedActivityLevels: BackendAllowedLevels): ResolvedActivityLevels | null => {
  if (!backendAllowedActivityLevels || !Array.isArray(backendAllowedActivityLevels.options)) {
    return null;
  }

  const options = backendAllowedActivityLevels.options
    .map((option) => {
      const value = String(option.value ?? '').trim();
      if (!value) return null;

      const label = String(option.label ?? '').trim() || toDisplayLabelFromValue(value);
      const palValue = typeof option.palValue === 'number' ? option.palValue : NaN;
      const iomPaMale = typeof option.iomPaMale === 'number' ? option.iomPaMale : undefined;
      const iomPaFemale = typeof option.iomPaFemale === 'number' ? option.iomPaFemale : undefined;
      const description = String(option.description ?? '').trim() || (
        Number.isFinite(palValue)
          ? formatActivityDescription(palValue, iomPaMale, iomPaFemale)
          : ''
      );

      return {
        value,
        label,
        description,
        palValue: Number.isFinite(palValue) ? palValue : 0,
        isDefault: Boolean(option.isDefault),
        ...(typeof iomPaMale === 'number' ? { iomPaMale } : {}),
        ...(typeof iomPaFemale === 'number' ? { iomPaFemale } : {}),
      } satisfies ActivityLevelOption;
    })
    .filter((option): option is ActivityLevelOption => Boolean(option));

  if (options.length === 0) return null;

  return {
    ageGroup: backendAllowedActivityLevels.ageGroup || 'Custom',
    options,
  };
};

const buildBackendKeyMap = (backendAllowedActivityLevels: BackendAllowedLevels): Map<ActivityLevelKey, BackendAllowedLevels['options'][number]> => {
  const map = new Map<ActivityLevelKey, BackendAllowedLevels['options'][number]>();
  if (!backendAllowedActivityLevels || !Array.isArray(backendAllowedActivityLevels.options)) {
    return map;
  }

  backendAllowedActivityLevels.options.forEach((option) => {
    const keyFromValue = getActivityLevelKey(String(option.value ?? ''));
    const keyFromLabel = getActivityLevelKey(String(option.label ?? ''));
    const key = keyFromValue ?? keyFromLabel;
    if (key && !map.has(key)) {
      map.set(key, option);
    }
  });

  return map;
};

const getBackendOptionForLocalKey = (
  localKey: ActivityLevelKey,
  backendOptionByKey: Map<ActivityLevelKey, BackendAllowedLevels['options'][number]>
): BackendAllowedLevels['options'][number] | null => {
  const candidateKeys = getEquivalentKeys(localKey);
  for (const candidateKey of candidateKeys) {
    const option = backendOptionByKey.get(candidateKey);
    if (option) return option;
  }
  return null;
};

export const resolveAllowedActivityLevels = ({
  age,
  biologicalSex = '',
  backendAllowedActivityLevels,
}: {
  age: number | null | undefined;
  biologicalSex?: BiologicalSex | null;
  backendAllowedActivityLevels?: BackendAllowedLevels;
}): ResolvedActivityLevels | null => {
  if (!Number.isFinite(age)) {
    return normalizeBackendOnlyOptions(backendAllowedActivityLevels ?? null);
  }

  const normalizedAge = Math.max(0, Number(age));
  const normalizedSex: BiologicalSex =
    biologicalSex === 'male' || biologicalSex === 'female'
      ? biologicalSex
      : '';

  const profile = getAgeProfile(normalizedAge);
  if (!profile) {
    return normalizeBackendOnlyOptions(backendAllowedActivityLevels ?? null);
  }

  const backendOptionByKey = buildBackendKeyMap(backendAllowedActivityLevels ?? null);
  const seenValues = new Set<string>();

  const options = profile.options
    .filter((option) => shouldIncludeOptionForSex(option, normalizedSex))
    .map((localOption) => {
      const backendOption = getBackendOptionForLocalKey(localOption.key, backendOptionByKey);
      const value = String(backendOption?.value ?? localOption.value).trim();
      if (!value) return null;

      const normalizedValue = value.toLowerCase();
      if (seenValues.has(normalizedValue)) {
        return null;
      }
      seenValues.add(normalizedValue);

      const palValue = localOption.palValue;
      const iomPaMale = localOption.iomPaMale;
      const iomPaFemale = localOption.iomPaFemale;

      return {
        value,
        label: localOption.label,
        description: localOption.description || String(backendOption?.description ?? '').trim() || formatActivityDescription(palValue, iomPaMale, iomPaFemale),
        palValue,
        isDefault: backendOption?.isDefault ?? Boolean(localOption.isDefault),
        ...(typeof iomPaMale === 'number' ? { iomPaMale } : {}),
        ...(typeof iomPaFemale === 'number' ? { iomPaFemale } : {}),
      } satisfies ActivityLevelOption;
    })
    .filter((option): option is ActivityLevelOption => Boolean(option));

  if (options.length === 0) {
    return normalizeBackendOnlyOptions(backendAllowedActivityLevels ?? null);
  }

  return {
    ageGroup: profile.ageGroup,
    options,
  };
};

export const getActivityLevelOptionByValue = (
  allowedActivityLevels: ResolvedActivityLevels | BackendAllowedLevels | null | undefined,
  value: string | null | undefined
): ActivityLevelOption | null => {
  if (!allowedActivityLevels || !value) return null;
  const normalizedValue = String(value).trim().toLowerCase();
  if (!normalizedValue) return null;

  const targetKey = getActivityLevelKey(normalizedValue);
  const matched = allowedActivityLevels.options.find((option) => {
    const optionValue = String(option.value ?? '').trim().toLowerCase();
    if (optionValue === normalizedValue) return true;
    if (!targetKey) return false;
    return getActivityLevelKey(optionValue) === targetKey || getActivityLevelKey(String(option.label ?? '')) === targetKey;
  });

  if (!matched) return null;

  return {
    value: String(matched.value),
    label: String(matched.label ?? ''),
    description: String(matched.description ?? ''),
    palValue: typeof matched.palValue === 'number' ? matched.palValue : 0,
    isDefault: Boolean(matched.isDefault),
    ...(typeof matched.iomPaMale === 'number' ? { iomPaMale: matched.iomPaMale } : {}),
    ...(typeof matched.iomPaFemale === 'number' ? { iomPaFemale: matched.iomPaFemale } : {}),
  };
};

export const getActivityLevelSelectOptions = (
  allowedActivityLevels: ResolvedActivityLevels | BackendAllowedLevels | null | undefined
): ActivityLevelSelectOption[] => {
  const seenValues = new Set<string>();

  return (allowedActivityLevels?.options ?? []).reduce<ActivityLevelSelectOption[]>((acc, option) => {
    const value = String(option.value ?? '').trim();
    const normalizedValue = value.toLowerCase();
    if (!normalizedValue || seenValues.has(normalizedValue)) {
      return acc;
    }

    seenValues.add(normalizedValue);
    acc.push({
      value,
      label: String(option.label ?? '').trim(),
      description: String(option.description ?? '').trim() || undefined,
    });
    return acc;
  }, []);
};
